/**
 * useFileUpload Hook
 *
 * Handles file uploads to SharePoint document libraries.
 * - Simple upload for files <4MB
 * - Resumable upload for files >=4MB (chunked upload)
 * - Tracks upload state (isUploading, error)
 *
 * Usage:
 * ```tsx
 * const { uploadFiles, isUploading, error } = useFileUpload({
 *   tenantId,
 *   siteId,
 *   folderId,
 *   onUploadComplete: () => refetchFolder(),
 * });
 *
 * const handleFilesSelected = async (files: File[]) => {
 *   await uploadFiles(files);
 * };
 * ```
 */

import { useState, useCallback } from 'react';
import { TenantApiService } from '../services/tenantApi';
import { useDelegatedToken } from './useDelegatedToken';

// Size threshold for simple vs resumable upload (4MB)
const SIMPLE_UPLOAD_LIMIT = 4 * 1024 * 1024;

// Chunk size for resumable uploads (10MB as per Microsoft recommendation)
const CHUNK_SIZE = 10 * 1024 * 1024;

interface UseFileUploadOptions {
  tenantId: string;
  siteId: string;
  folderId: string;
  onUploadComplete?: () => void;
}

interface UseFileUploadResult {
  uploadFiles: (files: File[]) => Promise<void>;
  uploadFolder: (files: File[]) => Promise<void>;
  isUploading: boolean;
  error: string | null;
}

/**
 * File interface that includes webkitRelativePath for folder uploads.
 */
interface FileWithPath extends File {
  readonly webkitRelativePath: string;
}

/**
 * Extract unique folder paths from files with webkitRelativePath.
 * Returns paths sorted by depth (parents before children).
 */
function extractFolderPaths(files: FileWithPath[]): string[] {
  const folderPaths = new Set<string>();

  for (const file of files) {
    const relativePath = file.webkitRelativePath;
    if (!relativePath) continue;

    // Get all parent folder paths from the file's relative path
    const parts = relativePath.split('/');
    // Remove the filename (last part)
    parts.pop();

    // Build paths incrementally: "a", "a/b", "a/b/c"
    let currentPath = '';
    for (const part of parts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      folderPaths.add(currentPath);
    }
  }

  // Sort by path depth (shorter paths first) to ensure parents are created before children
  return Array.from(folderPaths).sort((a, b) => {
    const depthA = a.split('/').length;
    const depthB = b.split('/').length;
    return depthA - depthB;
  });
}

/**
 * Get the parent folder path for a file.
 * Example: "myFolder/subfolder/file.txt" -> "myFolder/subfolder"
 */
function getParentFolderPath(file: FileWithPath): string {
  const relativePath = file.webkitRelativePath;
  if (!relativePath) return '';

  const parts = relativePath.split('/');
  parts.pop(); // Remove filename
  return parts.join('/');
}

/**
 * Upload a small file using simple upload.
 * Uses delegated endpoint when token is available for user attribution.
 */
async function uploadSmallFile(
  tenantId: string,
  siteId: string,
  folderId: string,
  file: File,
  delegatedToken: string | null
): Promise<void> {
  if (delegatedToken) {
    await TenantApiService.uploadFileDelegated(tenantId, siteId, folderId, file, delegatedToken);
  } else {
    await TenantApiService.uploadFile(tenantId, siteId, folderId, file);
  }
}

/**
 * Upload a large file using resumable upload (chunked).
 * Uses delegated endpoint when token is available for user attribution.
 */
async function uploadLargeFile(
  tenantId: string,
  siteId: string,
  folderId: string,
  file: File,
  delegatedToken: string | null
): Promise<void> {
  // Create upload session (delegated or non-delegated)
  const session = delegatedToken
    ? await TenantApiService.createUploadSessionDelegated(tenantId, siteId, folderId, file.name, delegatedToken)
    : await TenantApiService.createUploadSession(tenantId, siteId, folderId, file.name);

  // Upload file in chunks (chunks go directly to SharePoint regardless of delegation)
  const fileSize = file.size;
  let offset = 0;

  while (offset < fileSize) {
    const chunkEnd = Math.min(offset + CHUNK_SIZE, fileSize);
    const chunkBlob = file.slice(offset, chunkEnd);
    const chunk = new Uint8Array(await chunkBlob.arrayBuffer());

    const contentRange = `bytes ${offset}-${chunkEnd - 1}/${fileSize}`;

    const response = await TenantApiService.uploadChunk(
      session.uploadUrl,
      chunk,
      contentRange
    );

    // Check if upload is complete (response contains id)
    if (response.id) {
      break; // Upload complete
    }

    offset = chunkEnd;
  }
}

/**
 * Hook for uploading files to SharePoint.
 *
 * @param options.tenantId - The managed tenant ID
 * @param options.siteId - The SharePoint site ID
 * @param options.folderId - The target folder ID
 * @param options.onUploadComplete - Callback when upload completes (for cache invalidation)
 * @returns Object with uploadFiles function, isUploading state, and error
 */
export function useFileUpload({
  tenantId,
  siteId,
  folderId,
  onUploadComplete,
}: UseFileUploadOptions): UseFileUploadResult {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getDelegatedToken } = useDelegatedToken();

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setIsUploading(true);
      setError(null);

      try {
        // Get delegated token for user attribution
        const delegatedToken = await getDelegatedToken();

        // Upload files sequentially to avoid overwhelming the server
        for (const file of files) {
          if (file.size < SIMPLE_UPLOAD_LIMIT) {
            // Simple upload for small files
            await uploadSmallFile(tenantId, siteId, folderId, file, delegatedToken);
          } else {
            // Resumable upload for large files
            await uploadLargeFile(tenantId, siteId, folderId, file, delegatedToken);
          }
        }

        // Call completion callback (e.g., to invalidate cache)
        onUploadComplete?.();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Upload failed';
        setError(errorMessage);
      } finally {
        setIsUploading(false);
      }
    },
    [tenantId, siteId, folderId, onUploadComplete, getDelegatedToken]
  );

  /**
   * Upload a folder (files selected with webkitdirectory attribute).
   * Creates folder structure first, then uploads files to correct subfolders.
   * Uses delegated endpoints when token is available for user attribution.
   */
  const uploadFolder = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setIsUploading(true);
      setError(null);

      try {
        // Get delegated token for user attribution
        const delegatedToken = await getDelegatedToken();

        const filesWithPath = files as FileWithPath[];

        // Extract unique folder paths from webkitRelativePath
        const folderPaths = extractFolderPaths(filesWithPath);

        // Build folder path to ID mapping (start with base folder)
        const folderIdMap: Record<string, string> = {};

        // Create folder structure if there are folders to create (delegated or non-delegated)
        if (folderPaths.length > 0) {
          const response = delegatedToken
            ? await TenantApiService.createFoldersDelegated(tenantId, siteId, folderId, folderPaths, delegatedToken)
            : await TenantApiService.createFolders(tenantId, siteId, folderId, folderPaths);

          // Build mapping from folder path to folder ID
          for (const folder of response.created_folders) {
            if (folder.path) {
              folderIdMap[folder.path] = folder.id;
            }
          }
        }

        // Upload files to their respective folders
        for (const file of filesWithPath) {
          const parentPath = getParentFolderPath(file);
          // Use the created folder ID if available, otherwise use the base folder
          const targetFolderId = folderIdMap[parentPath] ?? folderId;

          if (file.size < SIMPLE_UPLOAD_LIMIT) {
            await uploadSmallFile(tenantId, siteId, targetFolderId, file, delegatedToken);
          } else {
            await uploadLargeFile(tenantId, siteId, targetFolderId, file, delegatedToken);
          }
        }

        // Call completion callback (e.g., to invalidate cache)
        onUploadComplete?.();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Folder upload failed';
        setError(errorMessage);
      } finally {
        setIsUploading(false);
      }
    },
    [tenantId, siteId, folderId, onUploadComplete, getDelegatedToken]
  );

  return {
    uploadFiles,
    uploadFolder,
    isUploading,
    error,
  };
}
